import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";
import { ArrowSort_1Icon } from "vega-icons";

import { useOverflowObserver } from "@vega-core/hooks/useOverflowObserver";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { Tooltip } from "../Tooltip/Tooltip";
import { TooltipInfoIcon } from "../Tooltip/TooltipInfoIcon";

const Table = forwardRef<
  HTMLTableElement,
  HTMLAttributes<HTMLTableElement> & {
    noOverflow?: boolean;
  }
>(({ className, noOverflow, ...props }, ref) => (
  <div
    className={cnTwMerge("relative w-full overflow-auto", {
      "overflow-hidden": noOverflow,
    })}
  >
    <table
      className={cnTwMerge(
        "w-full caption-bottom text-sm overflow-auto",
        className,
      )}
      ref={ref}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    className={cnTwMerge("[&_tr]:border-b", className)}
    ref={ref}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    className={cnTwMerge("[&_tr:last-child]:border-0", className)}
    ref={ref}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    className={cnTwMerge(
      "border-t bg-neutral-100/50 font-medium [&>tr]:last:border-b-0 dark:bg-neutral-800/50",
      className,
    )}
    ref={ref}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    className={cnTwMerge(
      "group border-b border-border/primary transition-colors",
      className,
    )}
    ref={ref}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHeadV2 = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement> & {
    className?: string;
    sortedHeader?: "asc" | "desc" | false;
    tooltip?: string;
    hideSorting?: boolean;
    noHeader?: boolean;
    isMobileSelector?: boolean;
    isLastPinned?: boolean;
    isScrolled?: boolean;
    align?: "center" | "left" | "right";
  }
>(
  (
    {
      children,
      className,
      sortedHeader,
      tooltip,
      hideSorting = true,
      noHeader,
      isMobileSelector,
      isLastPinned,
      isScrolled,
      align,
      ...props
    },
    ref,
  ) => {
    const [contentRef, isOverflowing] = useOverflowObserver<HTMLDivElement>({
      axis: "both",
      includeDescendants: true,
    });
    const isSorted = sortedHeader === "asc" || sortedHeader === "desc";

    const contentNode = (
      <span
        className={cnTwMerge("truncate min-w-0", {
          "text-right": align === "right",
          "text-left": align === "left",
          "text-center": isMobileSelector || align === "center",
        })}
        ref={contentRef}
      >
        {children}
      </span>
    );

    const finalContent = isLastPinned ? (
      <div className="relative h-full w-full contents">
        {contentNode}
        <div
          className={cnTwMerge(
            "absolute top-0 -right-2 bottom-0 w-2 bg-gradient-to-r from-background/component/20 to-transparent pointer-events-none defaultTransition",
            isScrolled ? "opacity-100" : "opacity-0 max-w-0",
          )}
        />
      </div>
    ) : (
      <div className={cnTwMerge("block w-full")}>
        <div
          className={cnTwMerge("flex gap-1 w-full items-center min-w-0", {
            "justify-end": align === "right",
            "justify-start": align === "left",
            "justify-center": isMobileSelector || align === "center",
          })}
        >
          {/* TODO: should always be contentNode but causes tooltip and sort icon to have too big a gap */}
          {tooltip ? children : contentNode}
          {!hideSorting ? (
            <ArrowSort_1Icon
              className={cnTwMerge("w-4 h-4 defaultTransition")}
            />
          ) : null}
          {tooltip ? (
            /* The header cell's onClick sorts the column; interacting with
               the info icon must not double as a sort trigger. */
            <span onClick={(event) => event.stopPropagation()}>
              <TooltipInfoIcon
                className={cnTwMerge({
                  "text-text/secondary": !isSorted,
                  "text-text/primary": isSorted,
                })}
                content={tooltip}
                tooltipContentClassname="text-text/secondary"
              />
            </span>
          ) : null}
        </div>
      </div>
    );

    const TooltipContent = isOverflowing ? (
      <Tooltip content={children}>
        <span className="cursor-pointer">{finalContent}</span>
      </Tooltip>
    ) : (
      finalContent
    );

    return (
      <th
        className={cnTwMerge(
          "px-4 [&:has([role=checkbox])]:px-6 min-w-10 body-small-medium h-10 whitespace-nowrap bg-background/subtle",

          {
            "text-text/secondary": !isSorted,
            "text-text/primary": isSorted,
          },
          className,
        )}
        ref={ref}
        {...props}
      >
        {noHeader && !isLastPinned ? null : TooltipContent}
      </th>
    );
  },
);

const TableHead = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement> & {
    className?: string;
    sortedHeader?: "asc" | "desc" | false;
    alignColumnRight?: boolean;
    tooltip?: string;
    hideSorting?: boolean;
    noHeader?: boolean;
  }
>(
  (
    {
      children,
      className,
      sortedHeader,
      alignColumnRight,
      tooltip,
      hideSorting = true,
      noHeader,
      ...props
    },
    ref,
  ) => {
    const isSorted = sortedHeader === "asc" || sortedHeader === "desc";

    return (
      <th
        className={cnTwMerge(
          "px-4 [&:has([role=checkbox])]:px-6 min-w-10 body-small-medium h-10 whitespace-nowrap bg-background/subtle",

          {
            "text-text/secondary": !isSorted,
            "text-text/primary": isSorted,
          },
          className,
        )}
        ref={ref}
        {...props}
      >
        {noHeader ? null : (
          <div
            className={cnTwMerge("flex items-center gap-1 w-full", {
              "justify-end": alignColumnRight,
              "justify-start": !alignColumnRight,
            })}
          >
            {children}
            {!hideSorting ? (
              <ArrowSort_1Icon
                className={cnTwMerge("w-4 h-4 defaultTransition")}
              />
            ) : null}
            {tooltip ? (
              /* The header cell's onClick sorts the column; interacting with
                 the info icon must not double as a sort trigger. */
              <span onClick={(event) => event.stopPropagation()}>
                <TooltipInfoIcon
                  className={cnTwMerge({
                    "text-text/secondary": !isSorted,
                    "text-text/primary": isSorted,
                  })}
                  content={tooltip}
                  tooltipContentClassname="text-text/secondary"
                />
              </span>
            ) : null}
          </div>
        )}
      </th>
    );
  },
);

TableHead.displayName = "TableHead";

const TableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement> & {
    className?: string;
  }
>(({ className, children, ...props }, ref) => (
  <td
    className={cnTwMerge(
      "h-16 break-phrases align-middle px-4 [&:has([role=checkbox])]:px-6 body-base-regular text-text/primary text-nowrap",
      className,
    )}
    ref={ref}
    {...props}
  >
    {children}
  </td>
));
TableCell.displayName = "TableCell";

const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    className={cnTwMerge(
      "mt-4 text-sm text-neutral-500 dark:text-neutral-400",
      className,
    )}
    ref={ref}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableHeadV2,
  TableRow,
};
